import type { MaterialSrc } from "common/materials/materialSrc";
import { degreeToRadian } from "common/utils/math";
import { stableStringify } from "common/utils/stableStringify";
import * as THREE from "three";
import { OrthographicCamera } from "three";
import { getActiveSharedWebGLServices } from "../activeSharedWebGLServices";
import { defaultRenderParamsForGPUTier } from "../defaultRenderParamsForGPUTier";
import { AOAlgorithm } from "../threejs/higharc/AOAlgorithm";
import {
  designMaterialBakeRepeatPeriodAndRotation,
  designMaterialBakesToFlatColor,
  designMaterialColorVariantsCanUseTemplateTexture,
  flatColorBakedMaterial,
} from "./designMaterialBakeColorMapUtils";
import {
  designMaterialBake,
  designMaterialBakeToBlob,
  designMaterialThumbnailOrthographicCamera,
} from "./designMaterialRenderImage";
import type { DesignMaterialLoadOptions } from "./designMaterialRuntimeConfig";
import { BakeColorTextureMode, ComponentViewMode } from "./designMaterialRuntimeConfig";
import type { DesignMaterialParamsPreprocessed } from "./designMaterialTypes";
import type { BakedColorTexture } from "./designMaterialTypes";
import type { LoadTextureFunc } from "./loadTextureFuncType";

// Limit the cache size to 100 items. We can use a large limit since this is only for testing use.
const TESTING_BAKED_COLOR_MAP_CACHE_SIZE = 100;

const bakeCamera = new OrthographicCamera();

interface BakeColorMapParams {
  params: DesignMaterialParamsPreprocessed;
  renderer: THREE.WebGLRenderer;
  loadTextureUrl: LoadTextureFunc;
  bakedColorTextureSrc?: MaterialSrc;
  hasSSFXRenderer: boolean;
  options: DesignMaterialLoadOptions;
  createDesignMaterial: (
    params: DesignMaterialParamsPreprocessed,
    renderer: THREE.WebGLRenderer,
    loadTextureUrl: LoadTextureFunc,
    hasSSFXRenderer: boolean,
    bakedColorTextureSrc: MaterialSrc | undefined,
    options: DesignMaterialLoadOptions,
  ) => Promise<THREE.MeshPhysicalMaterial>;
}

async function commonBakeColorMapSetup({
  params,
  renderer,
  loadTextureUrl,
  hasSSFXRenderer,
  options,
  createDesignMaterial,
}: BakeColorMapParams) {
  const services = getActiveSharedWebGLServices();
  const renderParams = await defaultRenderParamsForGPUTier(services);
  renderParams.renderPipelineParams = {
    ...renderParams.renderPipelineParams,
    aoAlgorithm: AOAlgorithm.NONE,
    transparentBackground: true,
  };
  const materialToBake = await createDesignMaterial(params, renderer, loadTextureUrl, hasSSFXRenderer, undefined, {
    ...options,
    bakeColorTexture: BakeColorTextureMode.REPEATING_LAYERS,
    componentViewMode: ComponentViewMode.bakedColor,
  });
  const { repeatPeriodX, repeatPeriodY, rotation } = designMaterialBakeRepeatPeriodAndRotation(params);
  designMaterialThumbnailOrthographicCamera(bakeCamera, {
    worldWidth: repeatPeriodX,
    worldHeight: repeatPeriodY,
    worldCenterX: repeatPeriodX * 0.5,
    worldCenterY: repeatPeriodY * 0.5,
  });
  bakeCamera.rotation.z = degreeToRadian(rotation);
  bakeCamera.updateMatrixWorld();
  return {
    material: materialToBake,
    imageRenderer: services.imageRenderer,
    renderParams,
    options: { width: 512, height: 512 },
    camera: bakeCamera,
  };
}

export async function designMaterialBakeColorMap(bakeColorMapParams: BakeColorMapParams): Promise<BakedColorTexture> {
  const bakeParams = await commonBakeColorMapSetup(bakeColorMapParams);
  return designMaterialBake(bakeParams);
}

export async function designMaterialBakeColorMapToBlob(bakeColorMapParams: BakeColorMapParams): Promise<Blob> {
  const bakeParams = await commonBakeColorMapSetup(bakeColorMapParams);
  return await designMaterialBakeToBlob(bakeParams);
}

async function bakeColorMapWithOptimizations(bakeColorMapParams: BakeColorMapParams) {
  if (designMaterialBakesToFlatColor(bakeColorMapParams.params)) {
    return flatColorBakedMaterial(bakeColorMapParams.params);
  }

  if (
    bakeColorMapParams.bakedColorTextureSrc !== undefined &&
    bakeColorMapParams.bakedColorTextureSrc.layerConfigId === undefined &&
    designMaterialColorVariantsCanUseTemplateTexture(bakeColorMapParams.params)
  ) {
    const bakeTemplateParams = {
      ...bakeColorMapParams,
      params: {
        ...bakeColorMapParams.params,
        color: "#ffffff",
      },
      bakedColorTextureSrc: undefined,
    };
    const bakeParams = await commonBakeColorMapSetup(bakeTemplateParams);
    const bakedTemplate = await designMaterialBake(bakeParams);
    return {
      texture: bakedTemplate.texture,
      color: new THREE.Color(bakeColorMapParams.params.color),
      worldWidth: bakedTemplate.worldWidth,
      worldHeight: bakedTemplate.worldHeight,
      rotation: bakedTemplate.rotation,
      loadedPromise: Promise.resolve(),
    };
  }

  return designMaterialBakeColorMap(bakeColorMapParams);
}

// Baked textures are not WebGLRenderer specific, they are CanvasTextures.
const bakedColorMapCache: { [key: string]: { promise: Promise<BakedColorTexture>; lastUsed: number } } = {};

/**
 * This is to be used only for debugging functionality, like testing baking on the material-appearance-test page.
 * It caches baked color maps in memory to avoid re-baking them.
 */
export function designMaterialCachedBakeColorMap(bakeColorMapParams: BakeColorMapParams): Promise<BakedColorTexture> {
  const key = stableStringify({ params: bakeColorMapParams.params, options: bakeColorMapParams.options });
  if (bakedColorMapCache[key] !== undefined) {
    bakedColorMapCache[key].lastUsed = Date.now();
    return bakedColorMapCache[key].promise;
  }

  // Find and delete the least recently used item from the cache
  if (Object.keys(bakedColorMapCache).length > TESTING_BAKED_COLOR_MAP_CACHE_SIZE) {
    let oldestKey = "";
    let oldestTime = Infinity;

    for (const key in bakedColorMapCache) {
      if (bakedColorMapCache[key].lastUsed < oldestTime) {
        oldestTime = bakedColorMapCache[key].lastUsed;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      void bakedColorMapCache[oldestKey].promise.then((map) => map.texture?.dispose());
      delete bakedColorMapCache[oldestKey];
    }
  }

  const bakedTexturePromise = bakeColorMapWithOptimizations(bakeColorMapParams);
  bakedColorMapCache[key] = { promise: bakedTexturePromise, lastUsed: Date.now() };
  return bakedTexturePromise;
}
